// src/components/pos/WebPromotionsPopup.jsx
import React, { useState, useEffect, useRef } from 'react';
import { X, Gift, Tag, Coffee, Percent, ChevronRight, Loader2, Check, Shield, RefreshCw, AlertCircle } from 'lucide-react';
import webPromotionService from '../../services/webPromotionService';
import toast from 'react-hot-toast';

/**
 * WebPromotionsPopup
 *
 * Shows a modal with all active web promotions for a customer.
 * The cashier selects a promotion → OTP is sent to customer → cashier enters
 * the OTP received from the customer → promotion is applied to the cart.
 *
 * Props:
 *   promotions      - array of WebCustomerPromotion objects
 *   customerMobile  - customer's mobile number (for OTP and claim API)
 *   subtotal        - current cart subtotal (for min-order check)
 *   onClaim         - callback(claimedPromotion, discountInfo) when verified & applied
 *   onClose         - callback when the modal is dismissed without claiming
 */
const REWARD_ICONS = {
  free_item:           <Coffee size={18} />,
  percentage_discount: <Percent size={18} />,
  fixed_discount:      <Tag size={18} />,
  size_upgrade:        <Gift size={18} />,
};

const REWARD_GRADIENTS = {
  free_item:           ['#059669', '#0d9488'],
  percentage_discount: ['#f97316', '#f59e0b'],
  fixed_discount:      ['#2563eb', '#06b6d4'],
  size_upgrade:        ['#7c3aed', '#8b5cf6'],
};

function rewardLabel(snap) {
  if (snap.reward === 'free_item')           return snap.freeProductName ? `Free ${snap.freeProductName}` : (snap.rewardLabel || 'Free Item');
  if (snap.reward === 'percentage_discount') return `${snap.discountValue ?? 0}% Off`;
  if (snap.reward === 'fixed_discount')      return `Rs. ${snap.discountValue ?? 0} Off`;
  if (snap.reward === 'size_upgrade')        return snap.rewardLabel || 'Size Upgrade';
  return snap.rewardLabel || 'Special Offer';
}

// ─── OTP STEP STATES ───────────────────────────────────────────────────────
// null        → promotion list (default)
// 'sending'   → sending OTP spinner
// 'verify'    → OTP entry form
// 'verifying' → verifying spinner
// ────────────────────────────────────────────────────────────────────────────

const WebPromotionsPopup = ({ promotions = [], customerMobile, subtotal = 0, onClaim, onClose, preselectedPromo = null }) => {
  // Promotion list state
  const [error, setError]                   = useState('');
  const [pendingPromo, setPendingPromo]     = useState(null); // promo awaiting OTP

  // OTP state
  const [otpStep, setOtpStep]               = useState(null); // null | 'sending' | 'verify' | 'verifying'
  const [otpId, setOtpId]                   = useState(null);
  const [otpDigits, setOtpDigits]           = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError]             = useState('');
  const inputRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];
  const hasInitializedRef = useRef(false);

  if (!promotions.length) return null;

  // ── OTP digit input helpers ──────────────────────────────────────────────
  const handleDigitChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const next = [...otpDigits];
    next[index] = value;
    setOtpDigits(next);
    if (value && index < 5) inputRefs[index + 1].current?.focus();
  };

  const handleDigitKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handleDigitPaste = (e) => {
    e.preventDefault();
    const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6).split('');
    const next = [...otpDigits];
    digits.forEach((d, i) => { if (i < 6) next[i] = d; });
    setOtpDigits(next);
    const focus = Math.min(digits.length, 5);
    inputRefs[focus].current?.focus();
  };

  // ── Send OTP ─────────────────────────────────────────────────────────────
  const handleApply = async (promo) => {
    const snap = promo.promotionSnapshot;
    const minVal = snap.minOrderValue || 0;
    if (minVal > 0 && subtotal < minVal) {
      setError(`Minimum order of Rs. ${minVal} required for this offer.`);
      return;
    }

    setError('');
    setPendingPromo(promo);
    setOtpStep('sending');
    setOtpDigits(['', '', '', '', '', '']);
    setOtpError('');

    const result = await webPromotionService.sendPromoOtp(customerMobile);

    if (!result.success) {
      setOtpStep(null);
      setPendingPromo(null);
      setError(`Failed to send OTP: ${result.error}`);
      return;
    }

    setOtpId(result.otpId);
    setOtpStep('verify');
    // Focus first digit after render
    setTimeout(() => inputRefs[0].current?.focus(), 100);

    if (result.smsSent === false) {
      toast('⚠️ SMS could not be sent. Ask customer for verbal code.', { duration: 5000 });
    } else {
      toast.success(`OTP sent to customer's phone!`);
    }
  };

  // Run automatically if preselectedPromo is passed
  useEffect(() => {
    if (preselectedPromo && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      handleApply(preselectedPromo);
    }
  }, [preselectedPromo]);

  // ── Resend OTP ───────────────────────────────────────────────────────────
  const handleResend = async () => {
    setOtpStep('sending');
    setOtpDigits(['', '', '', '', '', '']);
    setOtpError('');

    const result = await webPromotionService.sendPromoOtp(customerMobile);
    if (!result.success) {
      setOtpStep('verify');
      setOtpError(`Resend failed: ${result.error}`);
      return;
    }

    setOtpId(result.otpId);
    setOtpStep('verify');
    toast.success('OTP resent!');
    setTimeout(() => inputRefs[0].current?.focus(), 100);
  };

  // ── Verify OTP ───────────────────────────────────────────────────────────
  const handleVerify = async () => {
    const code = otpDigits.join('');
    if (code.length !== 6) {
      setOtpError('Please enter the complete 6-digit OTP.');
      return;
    }

    setOtpStep('verifying');
    setOtpError('');

    const result = await webPromotionService.verifyPromoOtp(otpId, code);

    if (!result.success) {
      setOtpStep('verify');
      setOtpError(result.error || 'Invalid OTP. Try again.');
      return;
    }

    // ✅ Verified! Build discount info and propagate up
    const snap = pendingPromo.promotionSnapshot;
    const discountInfo = {
      _id: pendingPromo._id,
      promotionId: pendingPromo.promotionId,
      title: snap.title,
      reward: snap.reward,
      discountValue: snap.discountValue || 0,
      freeProductId: snap.freeProductId || null,
      freeProductName: snap.freeProductName || null,
      rewardLabel: rewardLabel(snap),
      minOrderValue: snap.minOrderValue || 0,
    };

    toast.success('Promotion verified & applied! 🎉');
    onClaim?.(pendingPromo, discountInfo);
  };

  // ── Cancel OTP step ──────────────────────────────────────────────────────
  const handleCancelOtp = () => {
    setOtpStep(null);
    setPendingPromo(null);
    setOtpId(null);
    setOtpDigits(['', '', '', '', '', '']);
    setOtpError('');
  };

  const isOtpComplete = otpDigits.every(d => d !== '');

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div
      id="web-promotions-popup"
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)',
      }}
    >
      <div style={{
        background: '#111',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 20,
        width: '100%',
        maxWidth: 440,
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
      }}>

        {/* ── Header ────────────────────────────────────────────────────── */}
        <div style={{
          background: otpStep ? 'linear-gradient(135deg,#1e3a5f,#1e40af)' : 'linear-gradient(135deg,#f97316,#ea580c)',
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          transition: 'background 0.3s',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {otpStep ? <Shield size={18} color="white" /> : <Gift size={18} color="white" />}
            <div>
              <p style={{ color: 'white', fontWeight: 700, fontSize: 14, margin: 0 }}>
                {otpStep ? 'OTP Verification' : 'Web Offers Available! 🎁'}
              </p>
              <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11, margin: 0 }}>
                {otpStep
                  ? `Verifying for ${customerMobile}`
                  : `${promotions.length} offer${promotions.length !== 1 ? 's' : ''} — select one for this order`}
              </p>
            </div>
          </div>
          <button
            onClick={otpStep ? handleCancelOtp : onClose}
            style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, padding: '4px 8px', cursor: 'pointer', color: 'white' }}
          >
            <X size={16} />
          </button>
        </div>

        {/* ── OTP Step ──────────────────────────────────────────────────── */}
        {otpStep && (
          <div style={{ padding: '20px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Selected promo recap */}
            {pendingPromo && (() => {
              const snap = pendingPromo.promotionSnapshot;
              const [g1, g2] = REWARD_GRADIENTS[snap.reward] || ['#f97316', '#ea580c'];
              return (
                <div style={{
                  background: `linear-gradient(135deg,${g1}22,${g2}11)`,
                  border: `1px solid ${g1}44`,
                  borderRadius: 12,
                  padding: '12px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}>
                  <div style={{ background: `linear-gradient(135deg,${g1},${g2})`, borderRadius: 8, padding: 6, color: 'white', display: 'flex' }}>
                    {REWARD_ICONS[snap.reward]}
                  </div>
                  <div>
                    <p style={{ color: 'white', fontWeight: 700, fontSize: 13, margin: 0 }}>{snap.title}</p>
                    <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, margin: 0 }}>{rewardLabel(snap)}</p>
                  </div>
                </div>
              );
            })()}

            {/* Sending spinner */}
            {otpStep === 'sending' && (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#3b82f6', margin: '0 auto 12px' }} />
                <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, margin: 0 }}>
                  Sending OTP to customer's phone…
                </p>
              </div>
            )}

            {/* OTP input */}
            {(otpStep === 'verify' || otpStep === 'verifying') && (
              <>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, margin: 0 }}>
                    Enter the 6-digit code sent to the customer's phone
                  </p>
                </div>

                {/* Digit boxes */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                  {otpDigits.map((digit, i) => (
                    <input
                      key={i}
                      ref={inputRefs[i]}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={e => handleDigitChange(i, e.target.value)}
                      onKeyDown={e => handleDigitKeyDown(i, e)}
                      onPaste={handleDigitPaste}
                      disabled={otpStep === 'verifying'}
                      style={{
                        width: 44, height: 52,
                        textAlign: 'center',
                        fontSize: 22, fontWeight: 700,
                        background: digit ? 'rgba(59,130,246,0.2)' : 'rgba(255,255,255,0.06)',
                        border: `2px solid ${digit ? '#3b82f6' : 'rgba(255,255,255,0.15)'}`,
                        borderRadius: 10,
                        color: 'white',
                        outline: 'none',
                        transition: 'all 0.15s',
                      }}
                    />
                  ))}
                </div>

                {/* OTP error */}
                {otpError && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '8px 12px' }}>
                    <AlertCircle size={14} color="#f87171" />
                    <p style={{ color: '#f87171', fontSize: 12, margin: 0 }}>{otpError}</p>
                  </div>
                )}

                {/* Verify button */}
                <button
                  onClick={handleVerify}
                  disabled={!isOtpComplete || otpStep === 'verifying'}
                  style={{
                    background: isOtpComplete ? 'linear-gradient(135deg,#22c55e,#16a34a)' : 'rgba(255,255,255,0.07)',
                    border: 'none', borderRadius: 12, padding: '12px',
                    color: isOtpComplete ? 'white' : 'rgba(255,255,255,0.3)',
                    fontWeight: 700, fontSize: 14, cursor: isOtpComplete ? 'pointer' : 'not-allowed',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    transition: 'all 0.2s',
                  }}
                >
                  {otpStep === 'verifying'
                    ? <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Verifying…</>
                    : <><Check size={16} /> Verify & Apply Offer</>}
                </button>

                {/* Resend */}
                <button
                  onClick={handleResend}
                  disabled={otpStep === 'verifying'}
                  style={{
                    background: 'none', border: 'none',
                    color: 'rgba(255,255,255,0.4)', fontSize: 12,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  }}
                >
                  <RefreshCw size={12} /> Resend OTP
                </button>
              </>
            )}
          </div>
        )}

        {/* ── Promotions List ───────────────────────────────────────────── */}
        {!otpStep && (
          <>
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {promotions.map(promo => {
                const snap = promo.promotionSnapshot;
                const [g1, g2] = REWARD_GRADIENTS[snap.reward] || ['#f97316', '#ea580c'];
                const minVal = snap.minOrderValue || 0;
                const isBelowMinOrder = minVal > 0 && subtotal < minVal;

                return (
                  <div
                    key={promo._id}
                    style={{
                      background: '#1a1a1a',
                      border: `1px solid ${isBelowMinOrder ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.08)'}`,
                      borderRadius: 14,
                      overflow: 'hidden',
                      opacity: isBelowMinOrder ? 0.55 : 1,
                      transition: 'all 0.2s',
                    }}
                  >
                    {/* Colour strip */}
                    <div style={{
                      background: `linear-gradient(135deg,${g1},${g2})`,
                      padding: '10px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}>
                      <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 10, padding: 6, color: 'white', display: 'flex' }}>
                        {REWARD_ICONS[snap.reward]}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ color: 'white', fontWeight: 800, fontSize: 13, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {snap.title}
                        </p>
                        <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11, margin: 0, fontWeight: 600 }}>
                          {rewardLabel(snap)}
                        </p>
                      </div>
                      <span style={{
                        background: 'rgba(255,255,255,0.2)', borderRadius: 20,
                        padding: '2px 8px', color: 'white', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
                      }}>
                        {promo.usesLeft}× left
                      </span>
                    </div>

                    {/* Body */}
                    <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        {snap.description && (
                          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, margin: '0 0 4px' }}>{snap.description}</p>
                        )}
                        {minVal > 0 && (
                          <p style={{ color: isBelowMinOrder ? '#f87171' : 'rgba(255,255,255,0.4)', fontSize: 10.5, fontWeight: 600, margin: '0 0 4px' }}>
                            Min. Order: Rs. {minVal} {isBelowMinOrder && `(Need Rs. ${(minVal - subtotal).toFixed(2)} more)`}
                          </p>
                        )}
                        {promo.expiresAt && (
                          <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, margin: 0 }}>
                            Expires: {new Date(promo.expiresAt).toLocaleDateString('en-LK')}
                          </p>
                        )}
                      </div>

                      {/* Apply button */}
                      <button
                        id={`btn-claim-promo-${promo._id}`}
                        onClick={() => handleApply(promo)}
                        disabled={isBelowMinOrder}
                        style={{
                          background: isBelowMinOrder ? '#333' : `linear-gradient(135deg,${g1},${g2})`,
                          border: 'none',
                          borderRadius: 10,
                          padding: '8px 16px',
                          color: isBelowMinOrder ? '#666' : 'white',
                          fontWeight: 700,
                          fontSize: 12,
                          cursor: isBelowMinOrder ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          whiteSpace: 'nowrap',
                          minWidth: 90,
                          justifyContent: 'center',
                          boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
                        }}
                      >
                        {isBelowMinOrder ? 'Locked' : <><Shield size={12} /> Apply</>}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Error */}
            {error && (
              <div style={{ padding: '8px 16px', background: 'rgba(239,68,68,0.1)', borderTop: '1px solid rgba(239,68,68,0.2)' }}>
                <p style={{ color: '#f87171', fontSize: 12, margin: 0 }}>⚠️ {error}</p>
              </div>
            )}

            {/* Footer */}
            <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, margin: 0 }}>
                OTP verification required to apply
              </p>
              <button
                onClick={onClose}
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '6px 14px', color: 'rgba(255,255,255,0.5)', fontSize: 12, cursor: 'pointer' }}
              >
                Skip for now
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WebPromotionsPopup;
